/** @jsx jsx */
import ProducerPage from '@bottlebooks/gatsby-theme-event/src/components/ProducerPage/ProducerPage';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import {
  getCertificationsForId,
  extractProducerIdsForCharacteristics,
} from '@bottlebooks/gatsby-theme-event-pages/src/helpers/brandCertifications';
import getWfsPrice from './getWfsPrice';

export default function ProducerTemplate({
  data,
  pageContext,
  location,
  navigate,
}) {
  const { event, producer, previous, next, site } = afterQuery(data);

  return (
    <ProducerPage
      {...pageContext}
      event={event}
      producer={producer}
      exhibitor={producer.exhibitor}
      products={producer && producer.products}
      previous={previous}
      next={next}
      siteTitle={site?.siteMetadata?.title}
      location={location}
      navigate={navigate}
    />
  );
}

export const pageQuery = graphql`
  query wfs_ProducerTemplate(
    $id: String!
    $eventId: ID!
    $producerId: ID!
    $exhibitorId: ID!
    $locale: Bottlebooks_ContentLocale
    $previousId: String
    $nextId: String
  ) {
    bottlebooks {
      event(eventId: $eventId, locale: $locale) {
        eventId
        registrations(filter: { companyId: { eq: $exhibitorId } }) {
          nodes {
            ...bb_Registration_ProducerPage
          }
        }
        producer(exhibitorId: $exhibitorId, producerId: $producerId) {
          producerId
          products {
            nodes {
              productId
            }
          }
          ...bb_ProducerPage
        }
        registeredBrands(filter: { brandId: { eq: $producerId } }) {
          totalCount
          nodes {
            ...bb_RegisteredBrand_ProducerPage
          }
        }
        registeredProducts(filter: { producerId: { eq: $producerId } }) {
          nodes {
            ...bb_ProductHeaderDetails_RegisteredProduct
            ...useLink_bb_RegisteredProduct
            product {
              ...bb_ProducerPage_Product
            }
            registration {
              exhibitorType: customFieldValue(key: "exhibitorType") {
                __typename
                ... on Bottlebooks_SelectFieldValue {
                  key
                }
              }
            }
            wfsPrice: customFieldValue(key: "wfsPrice") {
              ...FieldValue
            }
          }
          ...brandCertifications_RegisteredProductConnection
        }
      }
    }

    producer(id: { eq: $id }) {
      ...ProducerTemplate_Producer
      products {
        productId
      }
    }
    previous: producer(id: { eq: $previousId }) {
      ...ChangeProducerLink
    }
    next: producer(id: { eq: $nextId }) {
      ...ChangeProducerLink
    }
    ...ProducerTemplate_Query
  }

  fragment ProducerTemplate_Producer on Producer {
    ...ProducerPage
    event {
      ...ProducerPage_Event
    }
  }

  fragment ProducerTemplate_Query on Query {
    site {
      siteMetadata {
        title
      }
    }
  }
`;

function afterQuery({ bottlebooks, producer, previous, next, site }) {
  const bb_producer = bottlebooks?.event?.producer;
  const registrations = bottlebooks?.event?.registrations;
  const registeredBrands = bottlebooks?.event?.registeredBrands;
  const registeredProducts = bottlebooks?.event?.registeredProducts.nodes;
  const bb_products = bb_producer?.products?.nodes;
  const enhancedProducer = {
    ...producer,
    ...bb_producer,
    ...registeredBrands?.nodes?.[0],
    ...getCertificationsForId({
      extractIdsFn: extractProducerIdsForCharacteristics,
      id: producer.producerId,
      event: bottlebooks?.event,
    }),
    exhibitor: registrations?.nodes?.[0],
    products: registeredProducts?.map((registeredProduct) => {
      if (!bb_products?.length) return registeredProduct;
      const bb_product = bb_products.find(
        (bb_product) => bb_product.productId === registeredProduct.productId
      );
      return {
        ...registeredProduct,
        ...registeredProduct.product,
        ...bb_product,
        exhibitor: registeredProduct.registration.profile,
        ...getWfsPrice({
          exhibitorType: registeredProduct.registration?.exhibitorType?.key,
          price: registeredProduct?.wfsPrice?.text,
        }),
      };
    }),
    registrationCount: registeredBrands?.totalCount,
  };
  return {
    producer: enhancedProducer,
    event: { eventId: bottlebooks?.event?.eventId },
    previous,
    next,
    site,
  };
}
